import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  ArrowLeft, 
  BookOpen, 
  Users, 
  Calendar, 
  Clock, 
  FileDown, 
  Settings,
  UserPlus,
  RefreshCw,
  Download,
  Trash2,
  Plus,
  ExternalLink,
  Shield,
  HelpCircle,
  Layers,
  Copy,
  ToggleLeft,
  Home
} from 'lucide-react';

const HelpPage = () => {
  const navigate = useNavigate();

  const helpTopics = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: BookOpen,
      description: 'Introduction to PTC Builder and basic concepts',
      content: [
        {
          question: 'What is PTC Builder?',
          answer: 'PTC Builder is a comprehensive platform for managing Parent-Teacher Conference scheduling in Veracross. It allows you to sync teacher data from Veracross, create conference schedules, and export them back to Veracross for parent booking.'
        },
        {
          question: 'How do I log in?',
          answer: 'Use your email address and password to log in at the login page. If your school has MFA (Multi-Factor Authentication) enabled, you\'ll need to enter a 6-digit code from your authenticator app after entering your password.'
        },
        {
          question: 'What is Multi-Factor Authentication (MFA)?',
          answer: 'MFA adds an extra layer of security to your account. When enabled, you\'ll need both your password AND a time-based code from an authenticator app (like Google Authenticator or Authy) to log in. School administrators can enable or disable MFA for user accounts.'
        },
        {
          question: 'What\'s the difference between School Admin and School User roles?',
          answer: 'School Admin: Can view and manage ALL data within the school, including other users\' conference sets, schedules, and exports. Can also manage user accounts and school settings.\n\nSchool User: Can only view and manage their OWN data. Each user has isolated data - their own teachers, conference sets, schedules, and exports. This allows multiple coordinators to work simultaneously without interfering with each other\'s work.'
        },
        {
          question: 'What\'s the typical workflow?',
          answer: '1. Sync Teachers: Pull your faculty list from Veracross\n2. Configure Teachers: Add Zoom URLs, assign rooms, exclude non-participating teachers\n3. Create Conference Set: Define which teachers participate and URL display options\n4. Create/Link Schedule: Define time slots for the conference date\n5. Generate Export: Create the final export set\n6. Review & Customize: Adjust individual slots as needed\n7. Download CSV: Export the file for Veracross import'
        }
      ]
    },
    {
      id: 'teachers',
      title: 'Managing Teachers',
      icon: Users,
      description: 'Syncing, configuring, and organizing teachers',
      content: [
        {
          question: 'How do I sync teachers from Veracross?',
          answer: 'Navigate to the Teachers page and click the "Sync Teachers" button. This will pull all active faculty members from your Veracross instance. You\'ll need valid Veracross API credentials configured in your school settings.'
        },
        {
          question: 'How do I sync rooms from Veracross?',
          answer: 'On the Teachers page, click the "Sync Rooms" button to pull all rooms from your Veracross instance. Rooms are required for teachers who will have "In-Person" or "Attendee Choice" attendance options.'
        },
        {
          question: 'How do I assign rooms to teachers?',
          answer: 'On the Teachers page, use the "In-Person Room" dropdown for each teacher to select a room. Teachers must have a room assigned to use "In-Person" or "Attendee Choice" attendance options.'
        },
        {
          question: 'How do I add Zoom URLs for teachers?',
          answer: 'You can add Zoom URLs in two ways:\n\n1. Individual Edit: On the Teachers page, enter the Conference URL and Password directly in each teacher\'s row.\n\n2. Bulk Import: Click "Import Teacher Online Conference URLs" to upload a CSV file with teacher IDs and their Zoom meeting URLs. First download the template using "Download Teacher Online Conference URLs Template" to see the required format.'
        },
        {
          question: 'How do I exclude a teacher from conferences?',
          answer: 'On the Teachers page, click "Exclude" next to the teacher. Excluded teachers won\'t appear when creating conference sets. Toggle "View Excluded" to see and restore excluded teachers.'
        },
        {
          question: 'How do I bulk exclude or include multiple teachers?',
          answer: 'Use the checkboxes to select multiple teachers, then click "Exclude/Include" in the bulk actions bar. Choose whether to exclude or include all selected teachers. This saves time when managing large numbers of teachers.'
        },
        {
          question: 'How do I assign a room to multiple teachers at once?',
          answer: 'Select multiple teachers using checkboxes, then click "Assign Room" in the bulk actions bar. Choose a room from the dropdown, and it will be assigned to all selected teachers. This is useful when multiple teachers share a common location like a gym or cafeteria.'
        }
      ]
    },
    {
      id: 'conferences',
      title: 'Conference Sets',
      icon: Calendar,
      description: 'Creating and managing conference configurations',
      content: [
        {
          question: 'What is a Conference Set?',
          answer: 'A Conference Set is a collection of teachers who will participate in a parent-teacher conference event. It defines which teachers are available, what school level it\'s for, and how meeting URLs are displayed to parents.'
        },
        {
          question: 'How do I create a Conference Set?',
          answer: 'Click "New Conference Set" on the Conference Sets page. Give it a name, select a school level, and choose your URL display preference. Then add teachers by selecting them from the list or using department filters.'
        },
        {
          question: 'What are the URL Display Options?',
          answer: 'Direct Join Meeting Link: Parents see a clickable "Join Meeting" button that takes them directly to the Zoom meeting.\n\nFriendly Link with Password: Parents see the Zoom meeting ID and password displayed, along with a link to join. This is useful if parents need to manually enter the meeting password.'
        },
        {
          question: 'How do I add teachers to a Conference Set?',
          answer: 'In the Conference Set detail page, go to Step 2 "Select Teachers". Click "Add Teachers" to open the selection modal. You can filter by department and select multiple teachers at once.'
        },
        {
          question: 'How do I add teachers by department?',
          answer: 'Click the "Add by Department" button in the Teachers section. Select one or more departments, and all non-excluded teachers from those departments will be added to the conference set. This is useful when setting up conferences by division or department.'
        },
        {
          question: 'How do I clone a Conference Set?',
          answer: 'Click the "Clone" button at the top of the Conference Set detail page. Enter a new name, and the system will create a copy of the conference set including all teacher selections, URL settings, and linked schedule sets. This is perfect for creating similar conferences for different dates or divisions.'
        },
        {
          question: 'What are Conference Templates?',
          answer: 'Templates save your conference configuration (teachers, settings) for reuse. Create a template from an existing conference set, then use it to quickly create new conferences with the same setup. Templates are perfect for recurring conference events like fall and spring conferences.'
        }
      ]
    },
    {
      id: 'schedules',
      title: 'Schedule Sets',
      icon: Clock,
      description: 'Creating time slots and managing schedules',
      content: [
        {
          question: 'What is a Schedule Set?',
          answer: 'A Schedule Set defines the time slots available for conferences on a specific date. It includes the conference date, time slots, and default attendance option (In-Person, Remote, or Attendee Choice).'
        },
        {
          question: 'How do I create time slots?',
          answer: 'When creating a Schedule Set, you can either:\n\n1. Generate slots automatically: Select "Generated" tab, enter a start time, end time, slot duration, and break time. The system will create slots for you.\n\n2. Create custom slots: Select "Custom" tab and manually add each time slot with specific start and end times. This is useful for irregular schedules.'
        },
        {
          question: 'Can I reuse Schedule Sets?',
          answer: 'Yes! Schedule Sets are reusable. You can link the same Schedule Set to multiple Conference Sets. The "Used By" column shows how many Conference Sets use each schedule.'
        },
        {
          question: 'How do I link a Schedule Set to a Conference?',
          answer: 'In the Conference Set detail page, go to Step 3 "Link Schedule Sets". Click "Link Schedule" and either select an existing schedule or create a new one directly from the modal.'
        },
        {
          question: 'How do I adjust all time slots at once?',
          answer: 'In the Schedule Set detail page, click "Adjust All Times". Choose how many minutes to shift (5, 10, 15, 30, or 60) and the direction (forward/later or backward/earlier). All time slots will be adjusted simultaneously.'
        },
        {
          question: 'How do I bulk enable or disable time slots?',
          answer: 'In the Schedule Set detail page, use the checkboxes to select multiple time slots. Click "Enable/Disable" in the bulk actions bar, then choose whether to enable or disable all selected slots. Disabled slots will not be included in exports.'
        }
      ]
    },
    {
      id: 'exports',
      title: 'Exporting to Veracross',
      icon: FileDown,
      description: 'Generating and customizing export files',
      content: [
        {
          question: 'How do I generate an export file?',
          answer: 'In the Conference Set detail page, after linking schedules, go to Step 4 "Export Sets" and click "Generate Export Set". This combines all linked schedules with all teachers into a single export set for review.'
        },
        {
          question: 'How do I customize individual slots?',
          answer: 'In the Export Set detail page, expand each teacher\'s section to see their time slots. You can:\n• Toggle slots on/off using the switch\n• Change attendance options using the dropdown\n• Edit start/end times by clicking on them\n• Delete unwanted slots'
        },
        {
          question: 'How do I download the CSV file?',
          answer: 'In the Export Set detail page, click "Generate & Download CSV". This creates a CSV file formatted for Veracross import. The file includes all active slots with the correct column headers.'
        },
        {
          question: 'How do I import the CSV into Veracross?',
          answer: 'In Veracross, go to System > Data Import. Select "Parent Teacher Conference Slots" as the import type. Upload the CSV file generated by PTC Builder. Review the preview and complete the import.'
        },
        {
          question: 'How do I bulk change attendance options?',
          answer: 'In the Export Set detail page, click "Change Attendance" in the Bulk Operations toolbar. Select the attendance option (Remote Only, In-Person Only, or Attendee Choice) and choose whether to apply to all slots or selected slots. Teachers without rooms will automatically skip In-Person and Attendee Choice options.'
        },
        {
          question: 'How do I copy attendance settings between teachers?',
          answer: 'Click "Copy Config" in the Bulk Operations toolbar. Select a source teacher whose attendance settings you want to copy, then select the target teachers. The attendance option for each time slot will be copied from the source to all selected targets.'
        },
        {
          question: 'How do I bulk delete slots?',
          answer: 'Click "Delete Slots" in the Bulk Operations toolbar. You can delete slots by:\n• Date: Remove all slots for a specific conference date\n• Time Range: Remove all slots within a time range (e.g., 12:00 PM to 1:00 PM for lunch)\n\nThis is useful for removing break periods or cancelled conference dates.'
        },
        {
          question: 'How do I remove a teacher from an export set?',
          answer: 'In the Export Set detail page, use the checkboxes next to each teacher\'s name to select them. When you have teachers selected, click "Remove X Teacher(s)" in the Bulk Operations toolbar. This will remove ALL slots for those teachers from the export set.'
        }
      ]
    },
    {
      id: 'bulk-operations',
      title: 'Bulk Operations',
      icon: Layers,
      description: 'Efficiently manage multiple items at once',
      content: [
        {
          question: 'What bulk operations are available for Teachers?',
          answer: 'Teachers Page bulk operations:\n\n• Bulk Exclude/Include: Select multiple teachers and toggle their excluded status\n• Bulk Room Assignment: Assign the same room to multiple teachers at once\n\nSelect teachers using checkboxes, and the bulk actions bar will appear with available options.'
        },
        {
          question: 'What bulk operations are available for Conference Sets?',
          answer: 'Conference Set bulk operations:\n\n• Add Teachers by Department: Add all teachers from selected departments at once\n• Clone Conference Set: Create a copy with all teachers and linked schedules\n• Save as Template: Save the configuration for reuse\n• Create from Template: Start a new conference from a saved template'
        },
        {
          question: 'What bulk operations are available for Schedule Sets?',
          answer: 'Schedule Set bulk operations:\n\n• Adjust All Times: Shift all time slots forward or backward by a set number of minutes\n• Bulk Enable/Disable Slots: Select multiple slots and enable or disable them all at once'
        },
        {
          question: 'What bulk operations are available for Export Sets?',
          answer: 'Export Set bulk operations:\n\n• Change Attendance: Apply an attendance option to all or selected slots\n• Copy Config: Copy attendance settings from one teacher to others\n• Delete Slots: Remove slots by date or time range\n• Remove Teachers: Remove all slots for selected teachers from the export'
        },
        {
          question: 'How do I select multiple items for bulk operations?',
          answer: 'Use the checkboxes that appear next to items in tables and lists. You can:\n• Click individual checkboxes to select/deselect items\n• Click the header checkbox to select/deselect all visible items\n• A bulk actions bar will appear showing the count and available actions'
        }
      ]
    },
    {
      id: 'users',
      title: 'Managing Users',
      icon: UserPlus,
      description: 'Adding and managing school user accounts (Admin only)',
      content: [
        {
          question: 'How do I add new users to my school?',
          answer: 'Go to "Manage Users" in the sidebar (School Admin only). Click "Add User" and fill in their details including name, email, and role. Choose between School Admin (full access) or School User (own data only).'
        },
        {
          question: 'How do I reset a user\'s password?',
          answer: 'In "Manage Users", find the user and click the menu button. Select "Send Password Reset". The user will receive an email with instructions to reset their password.'
        },
        {
          question: 'How do I enable MFA for a user?',
          answer: 'MFA can be enabled on a per-user basis. In "Manage Users", find the user and toggle the MFA setting. The user will be prompted to set up their authenticator app on their next login.'
        }
      ]
    },
    {
      id: 'setup',
      title: 'Veracross Integration Setup',
      icon: ExternalLink,
      description: 'Connecting PTC Builder to your Veracross instance',
      content: [
        {
          question: 'How do I set up PTC Builder with Veracross?',
          answer: 'Follow these steps to connect PTC Builder to your Veracross instance:\n\nStep 1: Create API Credentials in Veracross\n1. Log into Veracross as an administrator\n2. From the Rocketship, navigate to System/IT > Identity & Access Management\n3. Under SSO & Integrations, click "Add Integration Partner"\n4. Search for "PTC Builder by EDUx"\n5. Click "Start Integration" (requires OAuth_App_Admin Security role)\n6. Enable the required scopes:\n   • List Staff/Faculty\n   • Read Staff/Faculty\n   • List Academics: Rooms\n   • Read Academics: Rooms\n   • List Academics: Departments\n   • Read Academics: Departments\n7. Save and copy the Client ID and Client Secret\n\nStep 2: Configure PTC Builder\n1. Log into PTC Builder as School Admin\n2. Go to School Settings\n3. Enter your Veracross API credentials\n4. Click "Test Connection" to verify\n\nStep 3: Sync Your Data\n1. Navigate to the Teachers page\n2. Click "Sync Teachers" and "Sync Rooms"\n3. Your teachers, rooms, and departments will be imported'
        },
        {
          question: 'What is the Veracross School Route?',
          answer: 'Your Veracross School Route is the unique identifier for your school in the Veracross URL. It\'s the part that comes after "axiom.veracross.com/" in your Veracross URL.\n\nFor example, if your Veracross URL is https://axiom.veracross.com/springfield, then your school route is "springfield".'
        },
        {
          question: 'What permissions do I need in Veracross?',
          answer: 'To set up the PTC Builder integration in Veracross, you need:\n\n1. Veracross Administrator access\n2. The "OAuth_App_Admin" Security role enabled for your account\n\nIf you don\'t have these permissions, contact your Veracross administrator to either grant you access or set up the integration on your behalf.'
        }
      ]
    },
    {
      id: 'settings',
      title: 'School Settings',
      icon: Settings,
      description: 'Configuring school levels and security',
      content: [
        {
          question: 'How do I configure Veracross API credentials?',
          answer: 'Go to "School Settings" and enter your Veracross API credentials:\n\n• Client ID - From your Veracross integration setup\n• Client Secret - From your Veracross integration setup\n• School Route - Your school\'s identifier (found after axiom.veracross.com/ in your URL)\n\nThese credentials are required for syncing teachers, rooms, and departments from Veracross.'
        },
        {
          question: 'How do I manage School Levels?',
          answer: 'School Levels represent divisions like Elementary, Middle School, High School, etc. In School Settings, you\'ll see a list of configured levels.\n\nTo view the school levels configured in your Veracross database, visit: https://axiom.veracross.com/#/results/4609 (open in a new tab) and make sure that all school levels from your Veracross database appear in PTC Builder.\n\nClick "Add Level" to add new levels with their Veracross ID and description.'
        },
        {
          question: 'How do I view my subscription status?',
          answer: 'School Admins can view subscription status in School Settings. The Subscription card shows:\n• Current status (Trial, Active, or Expired)\n• Expiry date and days remaining\n• "Extend Subscription" button to make a payment\n\nTo extend your subscription, click the button and complete the payment process. Your subscription will be updated after payment confirmation.'
        },
        {
          question: 'What security measures protect my data?',
          answer: 'PTC Builder implements multiple layers of security:\n\n• Encryption in Transit: All data is encrypted using TLS 1.3\n• Encryption at Rest: Sensitive data (API credentials, Zoom URLs, passwords) is encrypted using AES-256\n• Access Control: Role-based access ensures users only see authorized data\n• Data Isolation: Each school\'s data is completely isolated\n• MFA Support: Optional two-factor authentication for added security'
        }
      ]
    },
    {
      id: 'tips',
      title: 'Tips & Best Practices',
      icon: HelpCircle,
      description: 'Helpful tips for getting the most out of PTC Builder',
      content: [
        {
          question: 'How do I handle teachers with no Zoom URL?',
          answer: 'Teachers without a Zoom URL will have blank virtual location fields in the export. If using "Remote Only" or "Attendee Choice", make sure all teachers have their Zoom URLs configured before generating exports.'
        },
        {
          question: 'What if a teacher doesn\'t have a room assigned?',
          answer: 'Teachers without rooms can only use "Remote Only" attendance. The system will automatically prevent "In-Person" or "Attendee Choice" options for these teachers and show a warning icon.'
        },
        {
          question: 'How do I handle schedule changes after generating an export?',
          answer: 'You have several options:\n• Edit individual slot times by clicking on them in the Export Set detail view\n• Use "Adjust All Times" in the Schedule Set to shift all times\n• Regenerate the entire export set if major changes are needed'
        },
        {
          question: 'Can I work on multiple conferences at once?',
          answer: 'Yes! You can create multiple Conference Sets and Schedule Sets. Each represents a different conference event. Schedule Sets can be reused across multiple Conference Sets, saving time for recurring events.'
        },
        {
          question: 'How do I set up conferences for different divisions?',
          answer: 'Create separate Conference Sets for each division (Elementary, Middle, High School). Use the School Level setting to categorize each conference. You can use the "Clone" feature to quickly duplicate a conference setup for a different division.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-outfit text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <BookOpen className="h-5 sm:h-6 w-5 sm:w-6 text-indigo-600" />
                Help & Documentation
              </h1>
              <p className="text-sm text-slate-500">Learn how to use PTC Builder effectively</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Quick Navigation */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-outfit text-lg flex items-center gap-2">
              <Home className="h-5 w-5 text-indigo-600" />
              Quick Navigation
            </CardTitle>
            <CardDescription>Jump to a specific topic</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {helpTopics.map((topic) => (
                <Button
                  key={topic.id}
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById(topic.id)?.scrollIntoView({ behavior: 'smooth' })}
                  className="flex items-center gap-1.5"
                >
                  <topic.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{topic.title}</span>
                  <span className="sm:hidden">{topic.title.split(' ')[0]}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Help Topics */}
        <div className="space-y-6">
          {helpTopics.map((topic) => (
            <Card key={topic.id} id={topic.id}>
              <CardHeader>
                <CardTitle className="font-outfit text-lg flex items-center gap-2">
                  <topic.icon className="h-5 w-5 text-indigo-600" />
                  {topic.title}
                </CardTitle>
                <CardDescription>{topic.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {topic.content.map((item, index) => (
                    <AccordionItem key={index} value={`${topic.id}-${index}`}>
                      <AccordionTrigger className="text-left hover:no-underline text-sm sm:text-base">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 whitespace-pre-line text-sm">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Reference Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="font-outfit text-lg">Quick Reference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-indigo-600" />
                  Attendance Options
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li><strong>In-Person Only:</strong> Room location only</li>
                  <li><strong>Remote Only:</strong> Virtual meeting URL only</li>
                  <li><strong>Attendee Choice:</strong> Both options</li>
                </ul>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <FileDown className="h-4 w-4 text-indigo-600" />
                  Export File Format
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li><strong>Date format:</strong> MM/DD/YYYY</li>
                  <li><strong>Time format:</strong> HH:MM AM/PM</li>
                  <li><strong>File type:</strong> CSV (Veracross compatible)</li>
                </ul>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4 text-indigo-600" />
                  User Roles
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li><strong>School Admin:</strong> Full school access</li>
                  <li><strong>School User:</strong> Own data only</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>Need more help? Email <a href="mailto:support@borrowageek.com" className="text-indigo-600 hover:underline">support@borrowageek.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
